import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  ReferenceLine,
  ComposedChart,
  Scatter,
  ScatterChart,
  ZAxis,
} from "recharts";
import { Box, Typography, Grid, Tooltip as TooltipMui } from "@mui/material";

// Helper to format large numbers
const formatValue = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(0);
};

export const StatCard = ({ title, value, subValue, color }: any) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      borderRadius: "10px",
      p: 2.5,
      border: "1px solid #D3E3FD",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "rgba(0, 0, 0, 0.5)",
        fontWeight: 700,
        letterSpacing: "0.5px",
      }}
    >
      {title}
    </Typography>
    <Typography variant="h4" sx={{ color: color, fontWeight: 800, my: 0.5 }}>
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{ color: "rgba(0, 0, 0, 0.4)", fontWeight: 500 }}
    >
      {subValue}
    </Typography>
  </Box>
);

export const ConsumptionTrendChart = ({ data }: { data: any[] }) => {
  const chartData = data.map((item) => ({
    name: new Date(item.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    kwh: item.totalKwh,
  }));

  return (
    <Box sx={{ height: 300, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorKwh" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1064ED" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#1064ED" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            interval={Math.floor(chartData.length / 8)}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            itemStyle={{ color: "#1064ED" }}
          />
          <Area
            type="monotone"
            dataKey="kwh"
            stroke="#1064ED"
            strokeWidth={2.5}
            fillOpacity={1}
            fill="url(#colorKwh)"
            name="Total kWh"
          />
          <ReferenceLine
            y={
              chartData.reduce((acc, curr) => acc + curr.kwh, 0) /
              (chartData.length || 1)
            }
            stroke="#F59E0B"
            strokeDasharray="5 5"
            label={{
              position: "right",
              value: "Average",
              fill: "#F59E0B",
              fontSize: 12,
              fontWeight: 700,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const RevenueByMonthChart = ({ data }: { data: any[] }) => {
  const chartData = data.map((item) => ({
    name: item.month, // e.g., "2026-02"
    revenue: item.totalRevenue,
  }));

  return (
    <Box sx={{ height: 300, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            itemStyle={{ color: "#1064ED" }}
          />
          <Bar dataKey="revenue" radius={[4, 4, 0, 0]} barSize={40}>
            {chartData.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index % 2 === 0 ? "#1064ED" : "#0D9488"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const BILL_STATUS_COLORS: any = {
  Draft: "#0D9488",
  Approved: "#1064ED",
  PartiallyPaid: "#EA580C",
  Cancelled: "#EF4444",
};

export const BillStatusChart = ({ data }: { data: any[] }) => {
  const total = data.reduce((acc, item) => acc + item.count, 0);

  return (
    <Box
      sx={{ height: 300, width: "100%", display: "flex", alignItems: "center" }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="40%"
            cy="50%"
            innerRadius={65}
            outerRadius={95}
            paddingAngle={0}
            dataKey="count"
            nameKey="status"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={BILL_STATUS_COLORS[entry.status] || "#8884d8"}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            iconType="circle"
            formatter={(value) => (
              <span
                style={{
                  color: "rgba(0,0,0,0.7)",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {value}
              </span>
            )}
          />
          <text
            x="40%"
            y="54%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#181B25"
            style={{ fontSize: "20px", fontWeight: "bold" }}
          >
            {(
              ((data.find((d) => d.status === "Draft")?.count || 0) /
                (total || 1)) *
              100
            ).toFixed(1)}
            %
          </text>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const TopConsumersChart = ({ data }: { data: any[] }) => {
  const chartData = data
    .map((item) => ({
      name: item.meterSerial,
      kwh: item.totalKwh,
    }))
    .reverse();

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 50, left: 5, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <YAxis
            dataKey="name"
            type="category"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.8)", fontSize: 12, fontWeight: 600 }}
            width={110}
          />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.03)" }}
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
            itemStyle={{ color: "#1064ED" }}
          />
          <Bar dataKey="kwh" radius={[0, 4, 4, 0]} barSize={25}>
            {chartData.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index % 2 === 0 ? "#EF4444" : "#22D3EE"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const EVENT_COLORS: any = {
  METERSTATUS: "#1064ED",
  PUSHEVENTS: "#EF4444",
  METERRESPONSE: "#F59E0B",
  POWERFAILUREEVENTS: "#10B981",
  TRANSACTIONEVENTS: "#8B5CF6",
};

export const EventTypesChart = ({ data }: { data: any[] }) => {
  const total = data.reduce((acc, item) => acc + item.count, 0);

  return (
    <Box sx={{ height: 300, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="40%"
            cy="50%"
            innerRadius={65}
            outerRadius={95}
            paddingAngle={0}
            dataKey="count"
            nameKey="eventType"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={EVENT_COLORS[entry.eventType] || "#8884d8"}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            iconType="circle"
            formatter={(value) => (
              <span
                style={{
                  color: "rgba(0,0,0,0.7)",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                {value}
              </span>
            )}
          />
          <text
            x="40%"
            y="54%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#181B25"
            style={{ fontSize: "20px", fontWeight: "bold" }}
          >
            {(
              ((data.find((d) => d.eventType === "METERSTATUS")?.count || 0) /
                (total || 1)) *
              100
            ).toFixed(1)}
            %
          </text>
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const WorkOrdersChart = ({ data }: { data: any }) => {
  if (!data || !data.byStatus) return null;

  const chartData = data.byStatus.map((item: any) => ({
    name: item.label,
    count: item.count,
  }));

  const COLORS: any = {
    Failed: "#EF4444",
    Completed: "#10B981",
    Cancelled: "#F59E0B",
  };

  return (
    <Box sx={{ height: 300, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={50}>
            {chartData.map((entry: any, index: number) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.name] || "#1064ED"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const LoadProfileHeatmap = ({ data }: { data: any[] }) => {
  const DAYS = ["Sat", "Fri", "Thu", "Wed", "Tue", "Mon", "Sun"];
  const HOURS = Array.from({ length: 24 }, (_, i) => i);

  // Create a 2D map for quick lookup
  const dataMap = new Map();
  let maxAvg = 0;
  data.forEach((item) => {
    const dayName = DAYS[item.dayOfWeek] || DAYS[item.dayOfWeek % 7];
    dataMap.set(`${dayName}-${item.hour}`, item.avgKwh);
    if (item.avgKwh > maxAvg) maxAvg = item.avgKwh;
  });

  const getCellColor = (val: number) => {
    if (!val) return "rgba(16, 100, 237, 0.05)";
    const ratio = val / (maxAvg || 1);
    // Gradient from Blue (#1064ED) to Orange (#F59E0B)
    if (ratio < 0.5) return `rgba(16, 100, 237, ${0.1 + ratio * 1.8})`;
    return `rgba(245, 158, 11, ${ratio})`;
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto", py: 2 }}>
      <Box sx={{ minWidth: 600 }}>
        <Grid container sx={{ flexWrap: "nowrap" }}>
          {/* Y-axis Labels */}
          <Box sx={{ width: 40, pr: 1 }}>
            {DAYS.map((day) => (
              <Box
                key={day}
                sx={{
                  height: 35,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(0,0,0,0.5)", fontWeight: 600 }}
                >
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Heatmap Grid */}
          <Box sx={{ flex: 1 }}>
            {DAYS.map((day) => (
              <Box key={day} sx={{ height: 35, display: "flex" }}>
                {HOURS.map((hour) => {
                  const val = dataMap.get(`${day}-${hour}`) || 0;
                  return (
                    <TooltipMui
                      key={hour}
                      title={`${day} ${hour}:00 - Avg ${val.toFixed(2)} kWh`}
                      arrow
                    >
                      <Box
                        sx={{
                          flex: 1,
                          m: 0.5,
                          borderRadius: "2px",
                          backgroundColor: getCellColor(val),
                          transition: "transform 0.2s",
                          "&:hover": {
                            transform: "scale(1.1)",
                            zIndex: 1,
                            cursor: "pointer",
                          },
                        }}
                      />
                    </TooltipMui>
                  );
                })}
              </Box>
            ))}

            {/* X-axis Labels */}
            <Box sx={{ display: "flex", mt: 1 }}>
              {HOURS.map((hour) => (
                <Box key={hour} sx={{ flex: 1, textAlign: "center" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(0,0,0,0.5)",
                      fontSize: "9px",
                      fontWeight: 600,
                    }}
                  >
                    {hour.toString().padStart(2, "0")}:00
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Legend */}
          <Box
            sx={{
              width: 60,
              pl: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="caption" sx={{ mb: 1, fontWeight: 700 }}>
              kWh
            </Typography>
            <Box
              sx={{
                height: 150,
                width: 15,
                background:
                  "linear-gradient(to top, rgba(16, 100, 237, 0.1), #1064ED, #F59E0B)",
                borderRadius: "4px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: 150,
                justifyContent: "space-between",
                ml: 1,
                position: "relative",
                top: -150,
                left: 15,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "10px" }}>
                {maxAvg.toFixed(2)}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: "10px" }}>
                0.00
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export const RevenueTrendComposedChart = ({ data }: { data: any[] }) => {
  const chartData = data.map((item) => ({
    name: item.month,
    revenue: item.totalRevenue,
    billCount: item.billCount,
  }));

  return (
    <Box sx={{ height: 350, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <YAxis
            yAxisId="left"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          />
          <Legend verticalAlign="top" height={36} />
          <Bar
            yAxisId="left"
            dataKey="revenue"
            fill="#10B981"
            radius={[4, 4, 0, 0]}
            barSize={60}
            name="Revenue"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="billCount"
            stroke="#F59E0B"
            strokeWidth={3}
            dot={{ r: 4, fill: "#F59E0B" }}
            name="Bill Count"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const CollectionEfficiencyChart = ({ data }: { data: any[] }) => {
  const chartData = data.map((item) => ({
    name: item.month,
    billed: item.billed,
    collected: item.collected,
    efficiency: item.efficiencyPct,
  }));

  return (
    <Box sx={{ height: 350, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <YAxis
            yAxisId="left"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          />
          <Legend verticalAlign="top" height={36} />
          <Bar
            yAxisId="left"
            dataKey="billed"
            fill="#6366F1"
            radius={[4, 4, 0, 0]}
            barSize={40}
            name="Billed"
          />
          <Bar
            yAxisId="left"
            dataKey="collected"
            fill="#10B981"
            radius={[4, 4, 0, 0]}
            barSize={40}
            name="Collected"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="efficiency"
            stroke="#F59E0B"
            strokeWidth={3}
            dot={{ r: 4, fill: "#F59E0B" }}
            name="Efficiency %"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const RevenueForecastChart = ({
  data,
}: {
  data: { actual: any[]; forecast: any[] };
}) => {
  const combinedData = [
    ...data.actual.map((d) => ({
      date: d.date.split("T")[0],
      actual: d.actual,
      forecast: null,
      lower: null,
      upper: null,
    })),
    ...data.forecast.map((d) => ({
      date: d.date.split("T")[0],
      actual: null,
      forecast: d.forecast,
      lower: d.lower,
      upper: d.upper,
      range: [d.lower, d.upper],
    })),
  ];

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={combinedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10, fontWeight: 500 }}
            tickFormatter={(val) => val.split("-").slice(1).join("/")}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
            }}
          />
          <Legend verticalAlign="top" height={36} />
          <Area
            type="monotone"
            dataKey="range"
            stroke="none"
            fill="rgba(16, 100, 237, 0.1)"
            name="95% CI"
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#6366F1"
            strokeWidth={3}
            dot={{ r: 3, fill: "#6366F1" }}
            name="Actual Revenue"
            connectNulls
          />
          <Line
            type="monotone"
            dataKey="forecast"
            stroke="#10B981"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Forecast"
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const SiteCostTrendChart = ({ data }: { data: any[] }) => {
  const colors = [
    "#6366F1",
    "#10B981",
    "#F59E0B",
    "#EF4444",
    "#7C3AED",
    "#EC4899",
  ];

  return (
    <Box sx={{ height: 350, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="date"
            allowDuplicatedCategory={false}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10, fontWeight: 500 }}
            tickFormatter={(val) =>
              val.split("T")[0].split("-").slice(1).join("/")
            }
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
            }}
          />
          <Legend verticalAlign="top" height={36} />
          {Array.isArray(data) &&
            data.map((siteData, index) => (
              <Line
                key={siteData.site || index}
                data={siteData.data}
                type="monotone"
                dataKey="movingAvg"
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
                name={siteData.site}
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const BillDistributionChart = ({ data }: { data: number[] }) => {
  const safeData = Array.isArray(data) ? data : [];
  const min = safeData.length > 0 ? Math.min(...safeData) : 0;
  const max = safeData.length > 0 ? Math.max(...safeData) : 1000;
  const bucketsCount = 30;
  const bucketSize = (max - min) / bucketsCount;
  const histogram = Array.from({ length: bucketsCount }, (_, i) => ({
    range: `${(min + i * bucketSize).toFixed(0)} - ${(min + (i + 1) * bucketSize).toFixed(0)}`,
    count: safeData.filter(
      (v) => v >= min + i * bucketSize && v < min + (i + 1) * bucketSize,
    ).length,
    mid: min + (i + 0.5) * bucketSize,
  }));

  const mean =
    safeData.length > 0
      ? safeData.reduce((a, b) => a + b, 0) / safeData.length
      : 0;
  const sorted = [...safeData].sort((a, b) => a - b);
  const median =
    safeData.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;
  const p95 =
    safeData.length > 0 ? sorted[Math.floor(sorted.length * 0.95)] : 0;

  return (
    <Box sx={{ height: 350, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={histogram}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="mid"
            type="number"
            domain={["dataMin", "dataMax"]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10, fontWeight: 500 }}
            tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
            }}
          />
          <Bar
            dataKey="count"
            fill="rgba(99, 102, 241, 0.6)"
            radius={[2, 2, 0, 0]}
            name="Bill Count"
          />
          <ReferenceLine
            x={mean}
            stroke="#10B981"
            strokeDasharray="3 3"
            label={{
              position: "top",
              value: "Mean",
              fill: "#10B981",
              fontSize: 10,
            }}
          />
          <ReferenceLine
            x={median}
            stroke="#F59E0B"
            strokeDasharray="3 3"
            label={{
              position: "top",
              value: "Median",
              fill: "#F59E0B",
              fontSize: 10,
            }}
          />
          <ReferenceLine
            x={p95}
            stroke="#EF4444"
            strokeDasharray="3 3"
            label={{
              position: "top",
              value: "P95",
              fill: "#EF4444",
              fontSize: 10,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const CorrelationScatterChart = ({ data }: { data: any[] }) => {
  const safeData = Array.isArray(data) ? data : [];
  const plotData = safeData
    .flatMap((site) =>
      (site.data || []).map((d: any) => ({
        consumption: d.consumption,
        cost: d.cost,
        site: site.site,
      })),
    )
    .filter((d) => d.consumption > 0 && d.cost > 0);

  return (
    <Box sx={{ height: 350, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            type="number"
            dataKey="consumption"
            name="Consumption"
            unit="kWh"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10 }}
          />
          <YAxis
            type="number"
            dataKey="cost"
            name="Cost"
            unit="$"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 10 }}
            tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`}
          />
          <ZAxis type="category" dataKey="site" name="Site" />
          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
            }}
          />
          <Legend verticalAlign="top" height={36} />
          <Scatter
            name="Site Correlation"
            data={plotData}
            fill="rgba(99, 102, 241, 0.5)"
          />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const BalanceAgingChart = ({ data }: { data: any[] }) => {
  return (
    <Box sx={{ height: 350, width: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="rgba(0,0,0,0.06)"
          />
          <XAxis
            dataKey="bucket"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(0,0,0,0.5)", fontSize: 12, fontWeight: 500 }}
            tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
            }}
          />
          <Bar
            dataKey="amount"
            fill="#6366F1"
            radius={[4, 4, 0, 0]}
            barSize={50}
          >
            {Array.isArray(data) &&
              data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={["#22D3EE", "#FACC15", "#F87171", "#B91C1C"][index % 4]}
                />
              ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export const TimeOfUseDonutChart = ({ data }: { data: any[] }) => {
  const COLORS = ["#22D3EE", "#FACC15", "#10B981", "#F87171"];

  return (
    <Box
      sx={{
        height: 350,
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={Array.isArray(data) ? data : []}
            cx="50%"
            cy="50%"
            innerRadius={80}
            outerRadius={120}
            paddingAngle={5}
            dataKey="value"
            nameKey="name"
          >
            {Array.isArray(data) &&
              data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "#FFFFFF",
              border: "1px solid #D3E3FD",
              borderRadius: "8px",
            }}
          />
          <Legend verticalAlign="bottom" height={36} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};
